<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
} from 'vue';
import {
	SiteBackgroundGradient,
	BackgroundImagePositionOptions,
	SiteBackgroundType,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import { getGradientCss } from '@zyro-inc/site-modules/utils/cssUtils';
import { BACKGROUND_TYPES } from '@zyro-inc/site-modules/constants/siteModulesConstants';

interface Props {
	type?: SiteBackgroundType;
	alt?: string;
	overlayOpacity?: string;
	src?: string;
	srcset?: string;
	isEager?: boolean;
	gradient?: SiteBackgroundGradient | null;
	color?: string | null;
	isFixed?: boolean;
	isMobile?: boolean;
	isOpacityDisabled?: boolean;
	backgroundImagePosition?: BackgroundImagePositionOptions;
	videoPoster?: string;
}

const props = withDefaults(defineProps<Props>(), {
	type: BACKGROUND_TYPES.COLOR,
	isMobile: false,
	isOpacityDisabled: false,
	backgroundImagePosition: 'center',
});

const isVideoBackground = computed(() => props.type === BACKGROUND_TYPES.VIDEO);
const isImageBackground = computed(() => props.type === BACKGROUND_TYPES.IMAGE);

const backgroundColor = computed(() => {
	if (props.type === BACKGROUND_TYPES.GRADIENT && props.gradient) {
		return getGradientCss(props.gradient);
	}

	if (props.isOpacityDisabled) {
		return props.type === BACKGROUND_TYPES.COLOR ? props.color : 'rgb(255, 255, 255)';
	}

	return props.type === BACKGROUND_TYPES.COLOR ? props.color : 'transparent';
});
const gradientBackgroundPosition = computed(() => {
	const angle = (props.gradient?.angle || 0) + 90;
	const rad = (angle * Math.PI) / 180;

	const xStart = `${Math.round(50 - 50 * Math.cos(rad))}%`;
	const yStart = `${Math.round(50 - 50 * Math.sin(rad))}%`;
	const xEnd = `${Math.round(50 + 50 * Math.cos(rad))}%`;
	const yEnd = `${Math.round(50 + 50 * Math.sin(rad))}%`;

	return {
		xStart,
		yStart,
		xEnd,
		yEnd,
	};
});

const isBackgroundOverlayShown = computed(() => [
	BACKGROUND_TYPES.IMAGE,
	BACKGROUND_TYPES.VIDEO,
].includes(props.type as any) && !props.isOpacityDisabled && props.overlayOpacity);

const backgroundOverlayOpacity = computed(() => {
	const overlayOpacity = props.overlayOpacity ? props.overlayOpacity : 0;

	return isBackgroundOverlayShown.value
		&& !props.isOpacityDisabled
		&& overlayOpacity;
});

const blockBackgroundRef = ref<HTMLDivElement | null>(null);

onMounted(() => {
	nextTick(() => {
		blockBackgroundRef.value?.classList.add('transition-with-bg');
	});
});
</script>

<template>
	<div
		ref="blockBackgroundRef"
		class="block-background"
		:class="{
			'block-background--fixed': isFixed,
			'block-background--animated-gradient': type === BACKGROUND_TYPES.GRADIENT && gradient?.isAnimated
		}"
	>
		<img
			v-if="isImageBackground"
			:key="src"
			v-qa="'block-background-image'"
			:alt="alt"
			:src="src"
			:srcset="srcset"
			width="100vw"
			sizes="(max-width: 500px) 800px, 100vw"
			:loading="isEager ? 'eager' : 'lazy'"
			class="block-background__image"
			:class="{ 'block-background__image--fixed': isFixed }"
		>
		<video
			v-if="isVideoBackground"
			:key="src"
			:src="src"
			autoplay
			muted
			loop
			playsinline
			webkit-playsinline
			disablepictureinpicture
			controlslist="nodownload noplaybackrate nofullscreen noremoteplayback"
			class="block-background__image"
			:class="{ 'block-background__video--fixed': isFixed }"
			:poster="videoPoster"
		/>
		<div
			v-if="isBackgroundOverlayShown"
			class="block-background__overlay"
			:class="{ 'block-background__overlay--fixed': isFixed }"
		/>
	</div>
</template>

<style lang="scss">
.block-background {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: $z-index-site-engine-block-background;
	overflow: hidden;
	background: v-bind(backgroundColor);

	// backface-visiblity improves sub-pixel rendering which in some cases can prevent gaps between blocks
	backface-visibility: hidden;

	&--animated-gradient {
    animation: animate-gradient 25s ease infinite;
		background-size: 400% 400%;

		@keyframes animate-gradient {
			0%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
			50%{background-position: v-bind('gradientBackgroundPosition.xEnd') v-bind('gradientBackgroundPosition.xEnd')}
			100%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
		}

		@keyframes animate-gradient {
			0%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
			50%{background-position: v-bind('gradientBackgroundPosition.xEnd') v-bind('gradientBackgroundPosition.xEnd')}
			100%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
		}

		@keyframes animate-gradient {
			0%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
			50%{background-position: v-bind('gradientBackgroundPosition.xEnd') v-bind('gradientBackgroundPosition.xEnd')}
			100%{background-position: v-bind('gradientBackgroundPosition.xStart') v-bind('gradientBackgroundPosition.yStart')}
		}
	}

	// Height transition is needed to fix background images zooming on scroll on mobile
	// because of disappearing/appearing browser bottom navigation bar
	transition: height 1ms linear 999s;

	// clip-path is needed for firefox, safari browsers without it fixed background covers all the page
	// -1px from bottom fixes 1px gap in chrome browsers
	&--fixed {
		bottom: -1px;
		clip-path: inset(0);
		backface-visibility: hidden;
		pointer-events: none;
	}

	&__image {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 0;
		display: block;
		width: 100%;

		// By using 101% instead of 100%, the bug that caused a white gap to appear after an image was fixed.
		// This gap occurred because the uploaded image had a smaller size and different aspect ratio compared to the section.
		height: 101%;
		object-fit: cover;
		object-position: v-bind('props.backgroundImagePosition')
	}

	&__image, &__video {
		&--fixed {
			position: fixed;
			height: 100vh;
		}
	}

	&__overlay {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 2;
		width: 100%;
		height: 100%;
		background-color: $color-dark;
		opacity: v-bind(backgroundOverlayOpacity);

		&--fixed {
			position: fixed;
			height: 100vh;
		}
	}
}

.transition-with-bg {
	transition: background-color 0.3s ease-in-out, height 1ms linear 999s;
}
</style>
